<template>
  <Page>
    <div class="page">
      <Title
        title="Building Schoolscapes"
        preheader="Campus Complex"
        shape="triangle"
        subtitle="A theory behind a new educational network, why it's needed, and how it could shape the future of urban institutions."
        subtitleDate="Posted Mar 2023"
      ></Title>
      <div class="content">
        <div class="section">
          <div class="spacer"></div>
          <div class="spacer"></div>
          <div class="toc">
            <div class="toc-header">
              <div class="toc-header-svg">
                <Shape shape="triangle"></Shape>
              </div>
              <div class="toc-header-title">Table of<br />Contents</div>
            </div>
            <div class="toc-rows">
              <a
                class="toc-row"
                v-for="(section, index) in sections"
                :href="'#' + section.id"
                :key="index"
              >
                <div class="toc-row-title">{{ section.title }}</div>
                <div
                  class="toc-row-scroll"
                  :style="{ opacity: calcRowOpacity(index) }"
                >
                  {{ formatWithCommas(section.scroll) }}px Away
                </div>
                <div class="toc-row-bottom"></div>
              </a>
            </div>
            <div class="toc-border"></div>
          </div>
          <div class="toc-feet">
            <div class="toc-foot-1"></div>
            <div class="toc-foot-2"></div>
          </div>
          <div class="full-width-line">
            <div class="full-width-gradient"></div>
          </div>
          <div class="spacer"></div>
          <div class="spacer"></div>
          <div class="spacer"></div>
          <!-- <Figure name="theory/cc-placesetting.png" appearance="wide" /> -->
          <Header first="Introduction"></Header>
          <p>
            In Autumn 2022, Other Internet and Leave Room for Thoughts, two
            young learning institutes, came together to cultivate a new
            fellowship program named Campus Complex. It was a month-long
            educational experiment that unfolded across the City of New York.
          </p>
          <p>
            The program turned a disconnected group of private offices and
            studios into a traversable network of learning environments for
            three builders, artists, and researchers. Each fellow received a
            passport, called a Learner Card, to our constellation of spaces and
            educators. Their mission was to use the tools and affordances of the
            Campus to further their educational endeavors.
          </p>
          <Figure
            appearance="wide"
            name="theory/program-artifacts.png"
            desc="A fellow, Jeramey, with his Invitation and Learner Card"
          />
          <p>
            The fellowship program was inspired by questions like: what if
            office spaces became more school-like? What if their doors were more
            open to creative collaboration? What if lifelong learners were free
            to move about instead of stuck inside a single institution?
          </p>
          <p>
            These were certainly big questions for a small experimental program.
            Our pilot only scratched the surface, but it produced an abundance
            of ideas on why and how to build dynamic schoolscapes—and shake off
            the notion that learning is something that happens only while
            sitting in front of a computer.
          </p>
          <p>
            We're passing these concepts on to you, dear learners and
            campus-builders, in hopes that you will find new inspirations and
            understandings for use in your own educational experiments.
          </p>
          <!-- <Figure name="theory/invitations.png" appearance="wide" />
          <p class="faded">
            <i>
              Fig 01. Left: Garage, a hardware studio and Campus Complex
              learning environment. Right: Autumn Fellow Jeremy Hampton posts a
              BeReal with his invitation to the fellowship program.</i
            >
          </p> -->
        </div>

        <div class="section">
          <Header first="Learning Style"></Header>
          <p>
            <i>Hey Campus Complex, what’s a schoolscape?</i>
            We define <b>Schoolscape</b> as an association of local
            organizations that augment their spaces with educational
            infrastructure, and encourage learners to explore freely across
            their network. The schoolscape enables a learning lifestyle where
            participants start by askng themselves "Where do I want to learn?"
            instead of "What do I want to learn?"
          </p>

          <p>
            We’ve discovered that a schoolscape’s style of learning should
            enable fellows to
            <a class="red" href="/#sense-your-surroundings"
              >Sense their Surroundings</a
            >, <a class="green" href="/#learn-by-moving">Learn by Moving</a>,
            and <a class="blue" href="/#employ-leisure">Employ Leisure</a>.
            Together, these ingredients generate an ambience of agency that is
            rare in traditional work and learning environments. They recover the
            responsiveness and aliveness that is so sorely needed in this time
            of institutional upheaval.
          </p>

          <Figure
            name="theory/factory.png"
            appearance="wide"
            desc="Carson at work in The Factory – the HQ for the Campus Complex Autumn Program"
          />

          <h4>
            Sense your Surroundings<span id="sense-your-surroundings"></span>
          </h4>
          <p>
            An art studio, a coworking space, a community center, a software
            startup. The places across a schoolscape are outfitted with all
            manner of equipment, materials, and ambience. Think sewing machines,
            fabric bins, power tools, wood and metalworking stations, good
            lighting, musical instruments, turntables, printers, time machines,
            and more.
          </p>
          <p>
            In well-equipped environments such as these, the architecture is a
            syllabus in itself. There’s no need to predefine a linear journey
            through a curriculum—the space invites you to explore your interests
            using any materials you find within an arm's reach.
          </p>
          <p>
            We call the people who design these places
            <b><i>Educator-Architects</i></b
            >. They share their knowledge and skills through the medium of place
            design and the affordances they place in the environment. The
            lessons of the Educator-Architects get transmitted over time.
            Repeated encounters with the spaces they've built change the way you
            think, make, and move about. The notion of centering the context
            rather than the material comes from Laurel Schwulst's
            <a
              href="https://www.are.na/laurel-schwulst/teaching-is-creating-maintaining-learning-environments"
              target="_blank"
              >research on Teaching</a
            >.
          </p>
          <p>
            We call the helpful guides at each of the learning environments
            across a schoolscape <b><i>Educator-Animators</i></b
            >. Educator-Animators activate the pop-up network and set the stage
            for learners to freely traverse across the schoolscape with all the
            support they need. They introduce the spaces and equipment, answer
            inquiries, and host Visiting Hours to help fellows further their own
            learning goals.
          </p>
          <p>
            These Educator-Animators nourish the curiosities that fellows are
            already cultivating instead of actively introducing new ones. The
            "Animator", a role that comes from the
            <a href="https://bahaipedia.org/Animator" target="_blank"
              >Baha'i tradition</a
            >, assumes that learners are already exceptionally capable—and that
            they have latent, underrated capacity. The Animator's job is to draw
            out that capacity from within the fellows, the way a fire tender
            fans the embers to keep them ablaze.
          </p>

          <h4>Learn by Moving<span id="learn-by-moving"></span></h4>
          <p>
            Campus Complex teaches and practices the art of educational
            migration. Instead of following predetermined routes, Fellows are
            radically free to explore on their own terms, at their own pace,
            following their own noses across the schoolscape. The field trip
            becomes the default mode of the educational experience, rather than
            an occasional exception to classroom quarantine.
          </p>

          <div class="audio-el">
            <p class="faded">
              The Subways are the Hallways<br /><span class="faded"
                >by AZL</span
              >
            </p>
            <audio
              controls
              src="/audio/The-Subway-is-the-Hallway.mp3"
              id="audio-subways"
              @play="audioIsPlaying"
              @pause="audioIsPaused"
            ></audio>
          </div>

          <p>
            Learner Cards are a simple but effective permission slip that
            encourage freedom of movement across the schoolscape. Installing
            this wayfinding signage in fellows' pockets instead of in the built
            environment allow us to pop-up a citywide campus without having to
            make expensive infrastructural investments. A small piece of
            well-designed paper can be as powerful a reality augmenter as
            high-tech goggles (we call this lo-fi AR!)
          </p>

          <p>
            As they traverse the Campus, the fellowship program invites them to
            imagine that even the in-between spaces can be classrooms (the
            subways are the hallways!). This nourishes a particular way of
            seeing: one that says even the most ordinary places can be
            educational environments if you're willing to look with Learner
            Eyes.
          </p>
          <p>
            The fellowship leaves a trace of migration patterns—literal lines of
            inquiry—which, taken together, chart the routes of our educational
            journeys.
          </p>
          <!-- <Figure name="theory/cards.png" appearance="wide" /> -->

          <h4>Employ Leisure<span id="employ-leisure"></span></h4>
          <p>
            The Campus Complex emphasizes city-play over home-work, retrieving
            the original meaning of the word school: skholē, a Greek concept
            meaning "spare time, leisure, rest, ease."
          </p>
          <p>
            Inspired by a timekeeping technology called
            <a href="https://www.paragonday.systems/" target="_blank"
              >Paragonday Systems</a
            >, we encourage fellows to live their ideal day. To enter a time
            zone where they "do what [they] want to do given the environment and
            circumstances", instead of following a preset path.
          </p>
          <p>
            Employing leisure means you aren't tethered to a deliverable you
            chose in the past. You aren't boxed into a calendrical grid or a
            schedule that dictates your every hour. You don't have to know ahead
            of time what you're going to make, or why. Each experiment is a
            before picture for a not-yet-determined after. Sometimes you can't
            know what you're making until after it's finished. The unconditional
            learning stipends that fellows receive at the beginning of the
            program encourage this playful openness to the present-future.
          </p>

          <video
            controls
            preload
            src="https://d1on0x8owq06rr.cloudfront.net/991eb70cff3b6cc27efea73fc919ad78-Autumn%20Archives%20-%20Blocks%20of%20Curiosity.mp4#t=0.001"
            @play="audioIsPlaying"
            @pause="audioIsPaused"
          >
            <source
              src="https://d1on0x8owq06rr.cloudfront.net/991eb70cff3b6cc27efea73fc919ad78-Autumn%20Archives%20-%20Blocks%20of%20Curiosity.mp4#t=0.001"
            />
          </video>
        </div>

        <div class="section">
          <Header first="Why Now?"></Header>
          <p>
            The Autumn Fellowship unfolded against the backdrop of our
            increasingly virtual work lives. We found ourselves spending more
            and more time sitting in front of screens. It began to feel like we
            were residents not of our neighborhoods but of our Zoom calls,
            Discord servers, Twitter spaces, and Telegram groups. Working and
            learning got conflated with scrolling and information processing,
            divorced from multi-sensory encounters with the outside world.
          </p>
          <p>
            These disembodied tools turned our attention away from the immediate
            surroundings and untethered us from our local environments. In the
            most extreme cases, fully virtual organizations even started
            referring to themselves as cities. Metaphorical cityscapes subbed in
            for literal ones, as in this manifesto from a digital organization
            called Metropolis:

            <span class="quote">
              Many have likened decentralized autonomous organizations to
              companies. But in their potential, decentralized autonomous
              organizations are more like cities ... By moving beyond
              company-centric models ... we open up the design space for
              building rich digital ecosystems that optimize for human thriving.
            </span>
          </p>

          <p>
            As more and more virtual employees begin to feel the dissociative
            effects of spending so much time in simulated environments, a
            difficult question emerges: how can we continue to engage with our
            locales even as our work becomes increasingly indifferent to
            geography, increasingly tethered to the digital economy?
          </p>
          <Figure name="theory/scaled-down-city.png" appearance="wide" />
          <p>
            The schoolscape is our answer. It's a social form that encourages
            digitally-native citizens to engage meaningfully with the physical
            environment. It takes the network structure that is so familiar to
            citizens of the web and employs it in our physical environments.
            When virtual organizations support schoolscapes, they partner with
            sites of creative production in a geographically bounded area,
            ensuring that some of their attention and resources are oriented
            towards the material world. The structure of the schoolscape is
            designed to reground our minds and our education, to weave our
            learning journeys into the fabric of the cityscape. It inspires us
            to break out of the sedentary mode of school/work and learn by
            exploring. To orient us towards the world outside the screen, the
            source of our livelihood, joys, and ongoing creative renewal.
          </p>

          <Figure name="theory/cards-together.png" />

          <div class="spacer"></div>

          <p>
            Thanks for stopping by today! To learn more about the program,
            checkout
            <a class="green" href="/placemaking">Placemaking the Campus</a> and
            <a class="blue" href="/traversing">Traversing the Campus</a>.
          </p>
          <div class="spacer"></div>

          <!-- <video
            controls
            preload
            src="https://d1on0x8owq06rr.cloudfront.net/94cbf6b2a02a07bcbb0b0cb325890b85-Autumn%20Archives%20-%20Shine%20the%20Light%20of%20Optimism.mp4#t=0.001"
          >
            <source
              src="https://d1on0x8owq06rr.cloudfront.net/94cbf6b2a02a07bcbb0b0cb325890b85-Autumn%20Archives%20-%20Shine%20the%20Light%20of%20Optimism.mp4#t=0.001"
            />
          </video> -->
          <div class="spacer"></div>
          <div class="spacer"></div>
        </div>
      </div>
      <Footer />
    </div>
  </Page>
</template>

<script>
import Page from "@/components/Page.vue";
import Title from "@/components/Title.vue";
import Header from "@/components/Header.vue";
import Footer from "@/components/Footer.vue";
import Figure from "@/components/Figure.vue";
import Shape from "@/components/Shape.vue";

export default {
  name: "Theory",
  components: {
    Page,
    Title,
    Header,
    Footer,
    Figure,
    Shape,
  },
  data() {
    return {
      sections: [
        {
          title: "Introduction",
          id: "introduction",
          scroll: 0,
        },
        {
          title: "Learning Style",
          id: "learning-style",
          scroll: 0,
        },
        {
          title: "Why Now?",
          id: "why-now",
          scroll: 0,
        },
      ],
      shouldPlaySoundscape: false,
    };
  },
  mounted() {
    window.addEventListener("scroll", this.throttle(this.onScroll, 30));
    // let audioSubways = document.getElementById("#audio-subways");

    // let audioWasPlaying = false;

    // audioSubways.addEventListener("play", (e) => {
    //   console.log(this.$store.state.soundscapeSound.playing);
    //   console.log("playing");
    // });

    // audioSubways.addEventListener("pause", (e) => {
    //   console.log(this.$store.state.soundscapeSound.playing);

    //   if (!this.$store.state.soundscapeSound.playing) {
    //     this.$store.dispatch("playSoundscape");
    //   }
    // });
  },
  created() {
    setTimeout(this.onScroll, 0);
    setTimeout(this.onScroll, 450);
    setTimeout(this.onScroll, 900);
  },
  beforeUnmount() {
    window.removeEventListener("scroll", this.onScroll);
  },
  computed: {},
  methods: {
    audioIsPlaying(e) {
      let soundscapeIsPlaying = this.$store.getters.isSoundscapePlaying;
      if (soundscapeIsPlaying) {
        // pause soundscape
        this.$store.dispatch("pauseSoundscape");
        this.shouldPlaySoundscape = true;
      }
    },
    audioIsPaused(e) {
      if (this.shouldPlaySoundscape) {
        // play soundscape
        this.$store.dispatch("playSoundscape");
        this.shouldPlaySoundscape = false;
      }
    },
    throttle(fn, wait) {
      var time = Date.now();
      return () => {
        if (time + wait - Date.now() < 0) {
          fn();
          time = Date.now();
        }
      };
    },
    onScroll() {
      let scrollPoint = window.scrollY + window.innerHeight / 2;
      this.sections.forEach((section) => {
        let newScroll =
          window.scrollY + this.getScrollPoint("#" + section.id) - scrollPoint;

        if (newScroll == null) {
          return;
        }

        if (newScroll < 0) {
          newScroll = 0;
        }
        if (newScroll.toString().includes(".5")) {
          newScroll = parseInt(newScroll.toString().split(".5")[0]);
        }
        section.scroll = newScroll;
      });
    },
    getScrollPoint(id) {
      let el = document.querySelector(id);
      if (el != null) {
        return Math.trunc(Math.floor(el.getBoundingClientRect().y));
      } else {
        return null;
      }
    },
    formatWithCommas(x) {
      return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    },
    calcRowOpacity(index) {
      if (index == 1) {
        return 0.8;
      } else if (index == 2) {
        return 0.6;
      } else if (index == 3) {
        return 0.4;
      } else if (index >= 4) {
        return 0.3;
      }

      return 1;
    },
  },
};
</script>

<style>
.audio-el {
  padding: 1rem;
  padding-bottom: 0;
  border-radius: 0.5rem;
  background: var(--tan-02);
  margin-top: 1rem;
  margin-bottom: 2rem;
}

.audio-el p {
  margin-bottom: 0;
  margin-top: 0.5rem;
  margin-left: 1rem;
}

.audio-el p .faded {
  opacity: 0.6;
}

.audio-el audio {
  width: 100%;
}

.audio-el audio::-webkit-media-controls-panel {
  background-color: var(--tan-02);
  padding: 0;
  margin: 0;
}

/* .section-anchor {
  position: relative;
  height: 0;
  margin-top: -8rem;
} */

h4 {
  position: relative;
}

h4 span {
  position: absolute;
  transform: translateY(-6.4rem);
}
</style>
